import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, catchError } from 'rxjs/operators';
import { Account, User, Role, Plan, Brand, AccountProfileData, AccountAssetData, PlanSubscription, AccountPreferredVendorData, ApiKey, UserCredential } from '../models/AccountModel';
import { Assessment, AccountAssessment } from '../models/AssessmentModel';
import { Observable, throwError } from 'rxjs';
import { SortDirection } from '@angular/material/sort';
import * as moment from 'moment';
import { IScoreData, IStatusQtrlyData, IAssessmentSummaryData } from './platform-api-types';
import { IntegrationPartner, ManageIntegrationModel } from '../models/ManageIntegrationModel';
import { ScanResult } from '../models/ScanResultModel';
import { SuccessResponse } from '../models/SuccessResponseModel';
import { Notification } from '../models/NotificationModel';
import { EmailTemplate } from '../models/EmailTemplateModel';
import { ManageAccountMappingModel } from '../models/ManageModel';
import { AutomateIntegrationModel } from '../models/AutomateIntegrationModel';
import { ScanTarget } from '../models/ScanTargetModel';
import { ScanConfiguration } from '../models/ScanConfigurationModel';
import { Metric } from '../models/MetricModel';

class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

export class ResultFilter {
  sortField: string;
  sortOrder: SortDirection;
  pageSize: number;
  startIndex: number;
  searchData: any;

  constructor(sortField?: string, sortOrder: SortDirection = 'asc', pageSize: number = 15, startIndex: number = 0, searchData: any = {}) {
    this.sortField = sortField;
    this.sortOrder = sortOrder;
    this.pageSize = pageSize || 15;
    this.startIndex = startIndex || 0;
    this.searchData = searchData;
  }

  getQueryParams(): HttpParams {
    let query = new HttpParams({encoder: new CustomEncoder()});
    if (this.sortField) {
      query = query.append('sort_field', this.sortField);
      if (this.sortOrder) {
        query = query.append('sort_direction', this.sortOrder);
      }
    }
    if (this.pageSize) {
      query = query.append('page_size', `${this.pageSize}`);
    }
    if (typeof this.startIndex === 'number') {
      query = query.append('start_index', `${this.startIndex}`);
    }
    for (const sKey of Object.keys(this.searchData)) {
      const val = this.searchData[sKey];
      if (Array.isArray(val)) {
        for (const iVal of val) {
          query = query.append(sKey, `${iVal}`);
        }
      } else {
        query = query.append(sKey, `${val}`);
      }
    }
    return query;
  }
}

export class PlanSearchCriteria extends ResultFilter {
  constructor(
    public active: boolean,
    public ispublic?: boolean,
    public parentAccountId?: number,
    public partnerKey?: string,
    public cAccountId?: number) {
    // TODO: this is here to allow for dropdown lists to have all plans
    //       enhancements will allow for dropdowns to lazy load items
    super('name', 'asc', 1000);
  }
  getQueryParams(): HttpParams {
    let query = super.getQueryParams();

    if (this.active != undefined) {
      query = query.append('active', `${this.active}`);
    }
    if (this.ispublic != undefined) {
      query = query.append('public', `${this.ispublic}`);
    }
    if (this.parentAccountId != undefined) {
      query = query.append('parent_account_id', `${this.parentAccountId}`);
    }
    if (this.partnerKey != undefined) {
      query = query.append('partner_key', `${this.partnerKey}`);
    }
    if (this.cAccountId != undefined) {
      query = query.append('account_id', `${this.cAccountId}`);
    }

    return query;
  }
}


export class Results<T = any> {
  results: T[];
  count: number;

  constructor(data, count) {
    this.results = data;
    this.count = count;
  }

  static createFromJson<T>(resultJson, c: new (objJson) => T): Results<T> {
    const objs = [];
    let resultsData = (resultJson as any).results;
    if (resultsData == undefined) {
      resultsData = (resultJson as any)
    }
    (resultsData).forEach(obj => {
      const newObj = new c(obj);
      objs.push(newObj);
    });

    let cnt = (resultJson as any).results_count;
    if (cnt == undefined) {
      cnt = objs.length;
    }
    return new Results<T>(objs, cnt);
  }
}

@Injectable()
export class PlatformApiService {

  constructor(private _http: HttpClient) { }

  getCredentials(): Observable<{ user: User, account: Account }[]> {
    return this._http.get(`${environment.platformApi}/credential`)
      .pipe(map(credentialsJson => {
        const credentials: { user: User, account: Account }[] = [];

        ((credentialsJson as any).credentials).forEach(cred => {
          const usr = new User(cred.user);
          const act = new Account(cred.account);
          credentials.push({ user: usr, account: act });
        })
        return credentials;
      })
      );
  }

  getNotifications(accountId?:number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<Notification>> {
    let url;

    if (accountId == undefined) {
      url =`${environment.platformApi}/notification`;
    } else {
      url =`${environment.platformApi}/account/${accountId}/notification`;
    }
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Notification)));
  }

  saveNotification(notification: Notification): Observable<Notification> {
    let url = `${environment.platformApi}/notification`;
    if (notification.id) {
      url = `${url}/${notification.id}`;
    }

    return this._http.post(url, {notification})
    .pipe(map(resp => new Notification(resp)));
  }

  saveNotificationStatus(accountId: number, userId: number, notificationId: number, isRead: boolean) {
    const url = `${environment.platformApi}/account/${accountId}/user/${userId}/notification/${notificationId}/readStatus`;

    return this._http.post(url, {isRead})
      .pipe(map(resp => {
        return 'OK';
      })
    );

  }

  getEmailTemplates(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<EmailTemplate>> {
    const url =`${environment.platformApi}/emailTemplate`;

    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, EmailTemplate)));
  }

  saveEmailTemplate(emailTemplate: EmailTemplate): Observable<EmailTemplate> {
    let url = `${environment.platformApi}/emailTemplate`;
    if (emailTemplate.id) {
      url = `${url}/${emailTemplate.id}`;
    }

    return this._http.post(url, {emailTemplate})
    .pipe(map(resp => new EmailTemplate(resp)));
  }

  linkSsoToAllAccounts(idToken): Observable<any> {
    const url = `${environment.platformApi}/user/ssoIdToken`;

    return this._http.post(url, {idToken})
      .pipe(map(resp => {
        return 'OK';
      })
    );
  }

  linkSsoToSpecificAccount(accountId: number, userId: number, idToken): Observable<any> {
    const url = `${environment.platformApi}/account/${accountId}/user/${userId}/ssoIdToken`;

    return this._http.post(url, {idToken})
      .pipe(map(resp => {
        return 'OK';
      })
    );
  }

  getManageSubAccount(parentAccountId, connectwiseCompanyId): Observable<Account> {
    return this._http.get(`${environment.platformApi}/account/${parentAccountId}/integration/manage/subAccount/${connectwiseCompanyId}`)
      .pipe(map(accountJson => {
        return new Account(accountJson);
      })
      );
  }

  getManageUnmappedSubAccounts(parentAccountId): Observable<Results<Account>> {
    return this._http.get(`${environment.platformApi}/account/${parentAccountId}/integration/manage/unmappedAccounts`)
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Account)));
  }

  getSubAccounts(parentAccountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<Account>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/account/${parentAccountId}/account`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Account)));
  }

  getSubAccount(parentAccountId: number, accountId): Observable<Account> {
    return this._http
      .get(`${environment.platformApi}/account/${parentAccountId}/account/${accountId}`)
      .pipe(map(jsonResults => new Account(jsonResults)));
  }

  createAccountAssessment(accountId, selectedAssesValue): Observable<any> {
    return this._http.post(`${environment.platformApi}/account/${accountId}/integration/manage/subAccount/assessment`, {selectedAssesValue})
    .pipe(map(resp => {
      return 'OK';
    })
  );
  }

  getAccount(accountId): Observable<Account> {
    return this._http
      .get(`${environment.platformApi}/account/${accountId}`)
      .pipe(map(jsonResults => new Account(jsonResults)));
  }

  getAllAccounts(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<Account>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/account`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Account)));
  }

  getAccountUsers(accountId, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<User>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/account/${accountId}/user`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, User)));
  }

  getUsers(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<User>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/user`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, User)));
  }

  getAccountUser(accountId: number, userId: number): Observable<User> {
    return this._http
      .get(`${environment.platformApi}/account/${accountId}/user/${userId}`)
      .pipe(map(userJson => {
        return new User(userJson);
      })
      );
  }

  saveAccountUser(accountId: number, usr: User): Observable<User> {
    return this._http
      .post(`${environment.platformApi}/account/${accountId}/user/${usr.id}`, { user: usr })
      .pipe(map(userJson => {
        return new User(userJson);
      })
      );
  }

  getAccountUserCredentials(accountId: number, userId: number): Observable<UserCredential[]> {
    return this._http
      .get(`${environment.platformApi}/account/${accountId}/user/${userId}/credential`)
      .pipe(map(respJson => {
        const creds: UserCredential[] = [];

        (respJson as any[]).forEach(c => {
          creds.push(new UserCredential(c));
        })

        return creds;
      }))
  }

  deleteAccountUserCredential(accountId: number, userId: number, credId: number) {
    return this._http.delete(`${environment.platformApi}/account/${accountId}/user/${userId}/credential/${credId}`)
  }

  getAccountIdFromReferralCode(referralCode: string): Observable<number> {
    return this._http
      .get(`${environment.platformApi}/referralCode/${referralCode}`)
      .pipe(map(acctId => {
        return +acctId;
      })
      );
  }

  getApiKeys(accountId: number): Observable<ApiKey[]> {
    return this._http
      .get(`${environment.platformApi}/account/${accountId}/apiKey`)
      .pipe(map(respJson => {
        const keys: ApiKey[] = [];

        (respJson as any[]).forEach(k => {
          keys.push(new ApiKey(k));
        });

        return keys;
      })
      );
  }

  createApiKey(accountId): Observable<ApiKey> {
    return this._http.post(`${environment.platformApi}/account/${accountId}/apiKey`, {})
      .pipe(map(resp => {
        return new ApiKey(resp);
      })
      );
  }

  saveAccount(accountId, accountJson, parentId?: number): Observable<Account> {
    let url = `${environment.platformApi}/account`;
    if (parentId) {
      url = `${url}/${parentId}/account`
    }
    if (accountId) {
      url = `${url}/${accountId}`;
    }

    return this._http.post(url, accountJson)
      .pipe(map(resp => {
        return new Account(resp)
      })
      );
  }

  deleteAccount(accountId) {
    return this._http.delete(`${environment.platformApi}/account/${accountId}`);
  }

  deletePlan(planId) {
    return this._http.delete(`${environment.platformApi}/plan/${planId}`);
  }

  deleteAssessmentTemplate(assessmentTemplateId) {
    return this._http.delete(`${environment.platformApi}/assessment/${assessmentTemplateId}`);
  }

  saveUser(accountId: number, userId: number, usr: User): Observable<User> {
    let url = `${environment.platformApi}/account/${accountId}/user`;
    if (userId) {
      url = `${url}/${userId}`;
    }

    return this._http.post(url, {user: usr})
      .pipe(map(resp => {
        return new User(resp);
      })
      );
  }

  deleteUser(accountId, userId) {
    return this._http.delete(`${environment.platformApi}/account/${accountId}/user/${userId}`);
  }

  setUserPreferences(accountId, userId, preferencesJson): Observable<any> {
    const url = `${environment.platformApi}/account/${accountId}/user/${userId}/preferences`;

    return this._http.put(url, preferencesJson)
      .pipe(map(resp => {
        return resp;
      })
      );
  }

  resendInvitation(accountId, userId) {
    const url = `${environment.platformApi}/account/${accountId}/user/${userId}/resendInvitation`;

    return this._http.post(url, null)
      .pipe(map(resp => {
        return new SuccessResponse(resp);
      }));
  }

  getAccountAssessments(parentAccountId: number, subAccountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<AccountAssessment>> {
    let url=`${environment.platformApi}/account/${parentAccountId}/account`
    if (subAccountId) {
      url = `${url}/${subAccountId}`
    }
    url = `${url}/accountAssessment`;
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, AccountAssessment)));
  }

  getAccountAssessment(parentAccountId: number, subAccountId: number, accountAssessmentId: number): Observable<AccountAssessment> {
    let url=`${environment.platformApi}/account/${parentAccountId}/account`
    if (subAccountId) {
      url = `${url}/${subAccountId}`
    }
    url = `${url}/accountAssessment/${accountAssessmentId}`;
    return this._http
      .get(url)
      .pipe(map(assessmentJson => {
        return new AccountAssessment(assessmentJson);
      })
      );
  }

  getAllAccountAssessments(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<AccountAssessment>> {
    const url=`${environment.platformApi}/accountAssessment`

    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, AccountAssessment)));
  }

  getCertificationAccountDetails(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<Account>> {
    const url= `${environment.platformApi}/getCertAcccountDetails`;
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Account)));
  }

  getCertificationAssessment(accountId: number, accountAssessmentId: number) : Observable<AccountAssessment> {
    const url= `${environment.platformApi}/account/${accountId}/accountAssessment/${accountAssessmentId}`;
    return this._http
      .get(url)
      .pipe(map(assessmentJson => {
        return new AccountAssessment(assessmentJson);
      })
      );
  }

  getCertificationAssmnt(accountAssessmentId: number) : Observable<AccountAssessment> {
    const url= `${environment.platformApi}/accountAssessment/${accountAssessmentId}`;
    return this._http
      .get(url)
      .pipe(map(assessmentJson => {
        return new AccountAssessment(assessmentJson);
      })
      );
  }

  getAssessments(resultFilter: ResultFilter = new ResultFilter(undefined,undefined,50)): Observable<Results<Assessment>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/assessment`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Assessment)));
  }

  saveAssessment(assessmentId, assessmentJson): Observable<Assessment> {
    let url = `${environment.platformApi}/assessment`;
    if (assessmentId) {
      url = `${url}/${assessmentId}`;
    }

    return this._http.post(url, assessmentJson)
      .pipe(map(resp => {
        return new Assessment(resp);
      })
      );
  }

  loadAssessment(assessmentId): Observable<Assessment> {
    return this._http.get(`${environment.platformApi}/assessment/${assessmentId}`)
      .pipe(map(resp => {
        return new Assessment(resp);
      })
      );
  }

  getAccountPlanSubscription(accountId, planSubscriptionId): Observable<PlanSubscription> {
    return this._http.get(`${environment.platformApi}/account/${accountId}/planSubscription/${planSubscriptionId}`)
      .pipe(map(resp => {
        return new PlanSubscription(resp);
      })
      );
  }

  getPlans(resultFilter: ResultFilter): Observable<Results<Plan>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/plan`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Plan)));
  }

  getPaymentStatus(accountId) {
      return this._http.get(`${environment.platformApi}/account/${accountId}/planSubscription`)
        .pipe(map(resp => {
          return new PlanSubscription(resp);
        })
      );
  }

  savePlan(planId, planJson): Observable<Plan> {
    let url = `${environment.platformApi}/plan`;
    if (planId) {
      url = `${url}/${planId}`;
    }

    return this._http.post(url, planJson)
      .pipe(map(resp => {
        return new Plan(resp);
      })
      );
  }

  getBrands(resultFilter: ResultFilter): Observable<Results<Brand>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/brand`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, Brand)));
  }

  saveBrand(brandId, brandJson): Observable<Brand> {
    let url = `${environment.platformApi}/brand`;
    if (brandId) {
      url = `${url}/${brandId}`;
    }
    return this._http.post(url, brandJson)
      .pipe(map(resp => {
        return new Brand(resp);
      })
    );
  }

  getBrand(brandId): Observable<Brand> {
    let url = `${environment.platformApi}/brand`;
    if (brandId) {
      url = `${url}/${brandId}`;
    }
    return this._http.get(url)
      .pipe(map(resp => {
        return new Brand(resp);
      })
      );
  }
  getAccountBrand(accountId, brandId) {
    let url = `${environment.platformApi}/account/${accountId}/brand`;
    if (brandId) {
      url = `${url}/${brandId}`;
    }
    return this._http
      .get(url)
      .pipe(map(brandJson => {
        const brand: Brand = new Brand(brandJson);
        return brand;
      }));
  }

  saveAccountBrand(accountId, brandId, brandJson): Observable<Brand> {
    let url = `${environment.platformApi}/account/${accountId}/brand`;
    if (brandId) {
      url = `${url}/${brandId}`;
    }
    return this._http.post(url, brandJson)
      .pipe(map(resp => {
        return new Brand(resp);
      })
    );
  }

  deleteAccountBrand(accountId, brandId): Observable<any> {
    return this._http.delete(`${environment.platformApi}/account/${accountId}/brand/${brandId}`);
  }

  deleteBrand(brandId): Observable<any> {
    return this._http.delete(`${environment.platformApi}/brand/${brandId}`);
  }

  getRoles(): Observable<Role[]> {
    const urlString = `${environment.platformApi}/role`;
    return this._http
      .get(urlString)
      .pipe(map(rolesJson => {
        const roles = [];

        (rolesJson as any[]).forEach(role => {
          const newRole = new Role({ id: role.id, name: role.name });
          roles.push(newRole);
        });

        return roles;
      })
      );
  }

  saveAssessmentResults(accountId, accountAssessmentId, surveyjsResults, scoredResults, currentPage, status, percentComplete): Observable<AccountAssessment> {
    const url = `${environment.platformApi}/account/${accountId}/accountAssessment/${accountAssessmentId}`;

    return this._http.post(url, { surveyResultsJson: surveyjsResults, scoredResultsJson: scoredResults, currentPage, status, percentComplete })
      .pipe(map(resp => {
        return new AccountAssessment(resp);
      })
    );
  }

  saveAccountAssessment(account:Account, accountAssessment: AccountAssessment): Observable<AccountAssessment> {
    let url = `${environment.platformApi}/account`;
    if (account && account.id != accountAssessment.accountId) {
      url = `${url}/${account.id}/account`;
    }
    url = `${url}/${accountAssessment.accountId}/accountAssessment`;

    if (accountAssessment.id) {
      url = `${url}/${accountAssessment.id}`;
    }
    return this._http.post(url, accountAssessment)
      .pipe(map(resp => {
        return new AccountAssessment(resp as AccountAssessment);
      })
    );
  }

  getAccountProfileData(accountId: number): Observable<AccountProfileData> {
    const urlString = `${environment.platformApi}/account/${accountId}/profile`;
    return this._http
      .get(urlString)
      .pipe(map(data => {
        return data as AccountProfileData;
      })
      );
  }

  saveAccountProfileData(accountId: number, data: AccountProfileData, parentId?: number): Observable<boolean> {
    let url = `${environment.platformApi}/account`;

    if (parentId) {
      url = `${url}/${parentId}/account`
    }

    url = `${url}/${accountId}/profile`;
    return this._http.post(url, data)
      .pipe(map(resp => {
        return true;
      })
      );
  }

  getAccountPreferredVendorData(accountId: number): Observable<AccountPreferredVendorData> {
    const urlString = `${environment.platformApi}/account/${accountId}/preferredVendor`;
    return this._http
      .get(urlString)
      .pipe(map(data => {
        return data as AccountPreferredVendorData;
      })
      );
  }

  saveAccountPreferredVendorData(accountId: number, data: AccountPreferredVendorData, parentId?: number): Observable<boolean> {
    let url = `${environment.platformApi}/account`;

    if (parentId) {
      url = `${url}/${parentId}/account`
    }

    url = `${url}/${accountId}/preferredVendor`;
    return this._http.post(url, data)
      .pipe(map(resp => {
        return true;
      })
      );
  }
  getAccountAssetData(accountId: number): Observable<AccountAssetData> {
    const urlString = `${environment.platformApi}/account/${accountId}/asset`;
    return this._http
      .get(urlString)
      .pipe(map(data => {
        return data as AccountAssetData;
      })
      );
  }

  saveAccountAssetData(accountId: number, data: AccountAssetData, parentId?: number): Observable<boolean> {
    let url = `${environment.platformApi}/account`;

    if (parentId) {
      url = `${url}/${parentId}/account`
    }

    url = `${url}/${accountId}/asset`;
    return this._http.post(url, data)
      .pipe(map(resp => {
        return true;
      })
      );
  }

  getAccountPaymentInfo(accountId: number): Observable<any> {
    const urlString = `${environment.platformApi}/account/${accountId}/paymentInfo`;
    return this._http
      .get(urlString)
      .pipe(map(data => {
        return data as any;
      })
      );
  }

  getAssessmentReport(accountId: number, userFirstName: string, acctName: string, acctAssessment: AccountAssessment, includeAttestation: boolean, includeAnalytics, theme: string) {
    let url = `${environment.platformApi}/account/${accountId}`;
    if (accountId !== acctAssessment.accountId) {
      url += `/account/${acctAssessment.accountId}`;
    }
    url += `/accountassessment/${acctAssessment.id}/report`;
    return this._http.get(url, {
      params: {
        userFirstName,
        accountName: acctName,
        includeAttestation: includeAttestation.toString(),
        includeAnalytics: includeAnalytics.toString(),
        reportTheme: theme
      },
      responseType: 'blob'
    })
    .pipe(map(resp => {
      return resp;
      })
    );
  }

  getAssessmentSummaryData(parentAccountId?: number, begDate?: Date, endDate?: Date): Observable<Array<IAssessmentSummaryData>> {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/assessmentSummaryData`;
    return this._http.get<Array<IAssessmentSummaryData>>(url, {
      params: {
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        begDate: begDate ? moment(begDate).format('YYYY-MM-DD') : ''
      }
    })
      .pipe(map(respJson => {
        return respJson;
      }));
  }

  // ClientRiskDistribution
  getScoreHistogramChartData(parentAccountId?: number, begDate?: Date, endDate?: Date, bucketSize?: number): Observable<Array<IScoreData>> {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/scoreHistogramChartData`;
    return this._http.get<Array<IScoreData>>(url, {
      params: {
        bucketSize: bucketSize ? bucketSize.toString() : '',
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        begDate: begDate ? moment(begDate).format('YYYY-MM-DD') : ''
      }
    })
      .pipe(map(respJson => {
        return respJson;
      }));
  }

  getAssessmentStatusChartData(parentAccountId?: number, date?: Date): Observable<Array<IStatusQtrlyData>> {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/assessmentStatusChartData`;
    return this._http.get<Array<IStatusQtrlyData>>(url, {
      params: {
        date: date ? moment(date).format('YYYY-MM-DD') : ''
      }
    })
      .pipe(map(respJson => {
        return respJson;
      }));
  }

  getAccountRiskChartData(parentAccountId?: number, date?: Date): Observable<Array<IStatusQtrlyData>> {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/accountRiskChartData`;
    return this._http.get<Array<IStatusQtrlyData>>(url, {
      params: {
        date: date ? moment(date).format('YYYY-MM-DD') : '',
      }
    })
      .pipe(map(respJson => {
        return respJson;
      }));
  }


  getPercentiles(parentAccountId?: number, date?: Date, profileField?: string, profileValue?: string) {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/percentiles`;

    return this._http
      .get(url, {
        params: toHttpParams({
          date: date ? moment(date).format('YYYY-MM-DD') : '',
          profileField: profileField || '',
          profileValue: profileValue || ''
        })
      })
      .pipe(map(respJson => {
        return respJson;
      })
      );
  }

  getPercentileCounts(parentAccountId?: number, date?: Date, profileField?: string, profileValue?: string) {
    let url = `${environment.platformApi}`;
    if (parentAccountId)
      url += `/account/${parentAccountId}`;
    url += `/analytics/percentileCounts`;

    return this._http
      .get(url, {
        params: toHttpParams({
          date: date ? moment(date).format('YYYY-MM-DD') : '',
          profileField: profileField || '',
          profileValue: profileValue || ''
        })
      })
      .pipe(map(respJson => {
        return respJson;
      })
      );
  }

  getIntegrationPartners(resultFilter: ResultFilter): Observable<Results<IntegrationPartner>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(`${environment.platformApi}/integrationPartner`, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, IntegrationPartner)));
  }

  getIntegrationPartner(partnerKey: string): Observable<IntegrationPartner> {
    return this._http.get(`${environment.platformApi}/integrationPartner/${partnerKey}`)
      .pipe(map(partnerJson => {
        return new IntegrationPartner(partnerJson);
      })
      );
  }

  saveIntegrationPartner(partner: IntegrationPartner) {
    let url = `${environment.platformApi}/integrationPartner`;
    if (partner.id) {
      url = `${url}/${partner.id}`;
    }

    return this._http.post(url, partner)
      .pipe(map(resp => {
        return new IntegrationPartner(resp)
      })
      );
  }

  getAccountIntegration(accountId:number): Observable<ManageIntegrationModel> {
    return this._http.get(`${environment.platformApi}/account/${accountId}/integration/manage`)
      .pipe(map(integrationAccountJson => {
        return new ManageIntegrationModel(integrationAccountJson);
      })
    );
  }

  saveAccountIntegration(accountId: number, integration: ManageIntegrationModel) {
    const url = `${environment.platformApi}/account/${accountId}/integration/manage`;
    return this._http.post(url, {partner:integration})
      .pipe(map(resp => {
        return new ManageIntegrationModel(resp)
      })
      );
  }

  saveSubAccountIntegration(accountId: number, subAccountId: number, integration: ManageIntegrationModel) {
    const url = `${environment.platformApi}/account/${accountId}/account/${subAccountId}/partner`;
    return this._http.post(url, {partner:integration})
      .pipe(map(resp => {
        return new ManageIntegrationModel(resp)
      })
      );
  }

  getManageUsers(accountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results> {
    const paramArray = resultFilter.getQueryParams();
    return this._http.get(`${environment.platformApi}/account/${accountId}/integration/manage/user`, { params: paramArray })
      .pipe(map(connectwiseUsersJson => {
        return Results.createFromJson(connectwiseUsersJson, Object);
      })
    );
  }

  addManageUser(accountId: number, user: any): Observable<any> {
    return this._http.post(`${environment.platformApi}/account/${accountId}/integration/manage/user`, { user });
  }

  revokeManageUser(accountId: number, user: any): Observable<any> {
    return this._http.request('DELETE', `${environment.platformApi}/account/${accountId}/integration/manage/user`, {body: {user}});
  }

  getManageCompanies(accountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<any>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http.get(`${environment.platformApi}/account/${accountId}/integration/manage/company`, { params: paramArray })
      .pipe(map(connectwiseCompaniesJson => {
        return Results.createFromJson(connectwiseCompaniesJson, Object);
      })
    );
  }

  getCSAllTenants(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<any>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http.get(`${environment.platformApi}/centralServicesAllTenants`, { params: paramArray })
      .pipe(map(jsonResults => { return Results.createFromJson(jsonResults, Object);})
    );
  }

  getAutomateClients(accountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<any>> {
    const paramArray = resultFilter.getQueryParams();
    return this._http.get(`${environment.platformApi}/account/${accountId}/integration/automate/client`, { params: paramArray })
      .pipe(map(automateClientsJson => {
        return Results.createFromJson(automateClientsJson, Object);
      })
    );
  }

  saveManageSubAccountMappings(accountId: number, mappings: ManageAccountMappingModel[], enableIntegration: boolean): Observable<any> {
    const url = `${environment.platformApi}/account/${accountId}/integration/manage/subAccountMapping`;
    return this._http.post(url, {mappings, enableIntegration});
  }

  getAutomateIntegration(accountId:number): Observable<AutomateIntegrationModel> {
    return this._http.get(`${environment.platformApi}/account/${accountId}/integration/automate`)
      .pipe(map(integrationAccountJson => {
        return new AutomateIntegrationModel(integrationAccountJson);
      })
    );
  }

  getMappedAutomateAccounts(accountId:number): Observable<Results<any>> {
    return this._http.get(`${environment.platformApi}/account/${accountId}/integrated/automate`)
      .pipe(map(integrationAccountJson => {
        return Results.createFromJson(integrationAccountJson, Object);
      })
    );
  }

  saveAutomateIntegration(accountId: number, integration: AutomateIntegrationModel) {
    const url = `${environment.platformApi}/account/${accountId}/integration/automate`;
    return this._http.post(url, {partner:integration})
      .pipe(map(resp => {
        return new AutomateIntegrationModel(resp)
      })
      );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(
      'Something bad happened; please try again later.');
  };

  emailExists(email) {
    return this._http.get(`${environment.platformApi}/email/${encodeURIComponent(email)}`)
      .pipe(map(respJson => {
        return true;
      }), catchError(() => new Observable((observer) => {
        observer.next(false);
        observer.complete();
      })));
  };

  getAccountScanTargets(parentAccountId: number, subAccountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<ScanTarget>> {
    let url = `${environment.platformApi}/account/${parentAccountId}`
    if (subAccountId) {
      url = `${url}/account/${subAccountId}`
    }
    url = `${url}/scanTarget`;
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, ScanTarget)));
  }

  getScanTargets(parentAccountId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<ScanTarget>> {
    const paramArray = resultFilter.getQueryParams();
    let url = `${environment.platformApi}/account/${parentAccountId}`
    url = `${url}/scanComputers`;
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, ScanTarget)));
  }

  getScanConfigurations(resultFilter: ResultFilter = new ResultFilter()): Observable<Results<ScanConfiguration>> {
    const url=`${environment.platformApi}/scan/configuration`
    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, ScanConfiguration)));
  }

  saveScanConfiguration(scanConfiguration: ScanConfiguration): Observable<ScanConfiguration> {
    let url = `${environment.platformApi}/scan/configuration`;
    if (scanConfiguration.id) {
      url = `${url}/${scanConfiguration.id}`;
    }

    return this._http.post(url, {scanConfiguration})
    .pipe(map(resp => new ScanConfiguration(resp)));
  }

  getScanTarget(parentAccountId: number, subAccountId: number, scanTargetId: number): Observable<ScanTarget> {
    let url = `${environment.platformApi}/account/${parentAccountId}`
    if (subAccountId) {
      url = `${url}/account/${subAccountId}`
    }
    url = `${url}/scanTarget/${scanTargetId}`;
    return this._http.get(url, {responseType: 'json'})
    .pipe(map(resp => {
      return new ScanTarget(resp)
    }));
  }

  getScanTargetResults(parentAccountId: number, subAccountId: number, scanTargetId: number, resultFilter: ResultFilter = new ResultFilter()): Observable<Results<ScanResult>> {
    let url = `${environment.platformApi}/account/${parentAccountId}`
    if (subAccountId) {
      url = `${url}/account/${subAccountId}`
    }
    url = `${url}/scanTarget/${scanTargetId}/result`;

    const paramArray = resultFilter.getQueryParams();
    return this._http
      .get(url, { params: paramArray })
      .pipe(map(jsonResults => Results.createFromJson(jsonResults, ScanResult)));
  }

  getScanTargetResult(parentAccountId: number, subAccountId: number, scanTargetId: number, scanResultId: number):Observable<ScanResult> {
    let url = `${environment.platformApi}/account/${parentAccountId}`;
    if (subAccountId) {
      url = `${url}/account/${subAccountId}`
    }
    url = `${url}/scanTarget/${scanTargetId}/result/${scanResultId}`;

    return this._http.get(url, {responseType: 'json'})
    .pipe(map(resp => {
      return new ScanResult(resp)
    }));
  };

  executeScan(parentAccountId:number, subAccountId: number, scanTargetId:number, scriptId: number, entityId: string) {
    let url = `${environment.platformApi}/account/${parentAccountId}`;
    if (subAccountId) {
      url = `${url}/account/${subAccountId}`
    }

    url = `${url}/scanTarget/${scanTargetId}/scan`;
    return this._http.post(url, {entityId, scriptId});
  }

  getReferralUrl(accountId: number):Observable<string>{
    const url = `${environment.platformApi}/account/${accountId}/referralUrl`;

    return this._http.get(url).pipe(map(resp => {
      return resp as string;
    })
    );
  }

  getProductInstanceDtls(resultFilter: ResultFilter = new ResultFilter()):Observable<any>{
    const paramArray = resultFilter.getQueryParams();
    const url = `${environment.platformApi}/centralService/productInstances`;
    return this._http.get(url, { params:paramArray }).pipe(map(resp => {
        return resp;
      }), catchError(() => new Observable((observer) => {
        observer.next(false);
        observer.complete();
      })));
  }

  sendSupportEmail(supportData: {}): Observable<any> {
    const url = `${environment.platformApi}/supportTicket`;

    return this._http.post(url, supportData)
      .pipe(map(resp => {
          return resp;
      })
    );
  }

  saveMetric(metric: Metric): Observable<any> {
    const url = `${environment.platformApi}/metric`;

    return this._http.post(url, {metric})
      .pipe(map(resp => {
        return 'OK';
      })
    );
  }
}

export class CustomQueryEncoderHelper implements HttpParameterCodec {
  encodeKey(k: string): string {
      return encodeURIComponent(k);
  }

  encodeValue(v: string): string {
      return encodeURIComponent(v);
  }

  decodeKey(k: string): string {
      return decodeURIComponent(k);
  }

  decodeValue(v: string): string {
      return decodeURIComponent(v);
  }
}

function toHttpParams(params) {
  return Object.getOwnPropertyNames(params)
      .reduce((p, key) => p.set(key, params[key]), new HttpParams({encoder: new CustomQueryEncoderHelper()}));
}